import { useFormikContext } from 'formik';
import { useNavigate } from 'react-router';
import { IconNames } from '@shipengine/giger-theme';
import { useTranslation } from '@packlink/translation-provider';
import { BrandedEmailTemplateParams, EmailsSettings, EmailType } from '@packlink/packlink-sdk';
import { useToggle } from '@packlink/utils';
import { useAmplitude, getViewAmplitudePayload } from '@hooks/useAmplitude';
import { Button, ButtonSize, ButtonVariant, Icon, IconSize, Switch, Typography } from '@shipengine/giger';
import { AmplitudeEventsBulkBrandedEmails } from '@BrandedEmails/metrics/amplitude';
import { useToggleActiveTemplate } from '@common/hooks/useToggleActiveTemplate';
import { APP_ROUTE } from '@pages/router/routes';
import { getIconStyles } from './HeaderStyles';
import { BackDialog } from '../BackDialog/BackDialog';
import { BaseHeader } from './BaseHeader';
import { ToggleDialog } from '../ToggleDialog/ToggleDialog';
import { emailTypesKeys } from '../EmailTemplatesList/constants';

interface HeaderProps {
    template?: EmailsSettings;
    isFormTouched?: boolean;
}

export function EditTemplateHeader({ template, isFormTouched }: HeaderProps): JSX.Element {
    const {
        t,
        i18n: { language: locale },
    } = useTranslation();
    const navigate = useNavigate();

    const { handleSubmit, isSubmitting, errors } = useFormikContext<BrandedEmailTemplateParams>();
    const { sendAmplitudeClickEvent } = useAmplitude();
    const { state: activeToggle, toggle: toggleActiveState } = useToggle(template?.active);
    const { state: isBackDialogOpen, toggle: toggleBackDialog } = useToggle();
    const { state: isActivateDialogOpen, toggle: toggleActivateDialog } = useToggle();
    const { toggleActiveTemplate } = useToggleActiveTemplate();

    const handleGoBack = () => {
        sendAmplitudeClickEvent(
            AmplitudeEventsBulkBrandedEmails.BRANDED_EMAILS_GO_BACK,
            getViewAmplitudePayload(locale),
        );

        if (isFormTouched) {
            toggleBackDialog();
        } else {
            navigate(APP_ROUTE.SETTINGS.BRANDED_EMAILS);
        }
    };

    const handleSave = async () => {
        sendAmplitudeClickEvent(
            AmplitudeEventsBulkBrandedEmails.BRANDED_EMAILS_SAVE_BRANDED_EMAIL_MODAL,
            getViewAmplitudePayload(locale),
        );
        await handleSubmit();
        toggleBackDialog();
        navigate(APP_ROUTE.SETTINGS.BRANDED_EMAILS);
    };

    const handleActivate = async () => {
        toggleActiveTemplate({
            emailType: template?.emailType as EmailType,
            active: !template?.active,
            onToggle: toggleActiveState,
            onSuccess: () => {
                toggleActivateDialog();
            },
            onError: () => {
                toggleActivateDialog();
            },
        });
    };

    const handleCancel = () => {
        toggleBackDialog();
        navigate(APP_ROUTE.SETTINGS.BRANDED_EMAILS);
    };

    const isFormBlocked = Object.keys(errors).length > 0 || isSubmitting;

    return (
        <>
            <BaseHeader>
                <Button
                    onClick={handleGoBack}
                    variant={ButtonVariant.TEXT}
                    size={ButtonSize.SMALL}
                    disabled={isFormBlocked}
                >
                    <Icon css={getIconStyles} name={IconNames.CHEVRON_LEFT} size={IconSize.SIZE_SMALL} />
                    <Typography variant="body2" bold>
                        {t('branded-emails.header.go-back')}
                    </Typography>
                </Button>
                <Switch
                    name={`is-enabled-${template?.emailType}`}
                    value={activeToggle}
                    isChecked={activeToggle}
                    disabled={isFormTouched || isFormBlocked}
                    onChange={toggleActivateDialog}
                >
                    {`${t('branded-emails.header.activate-template')}`}
                </Switch>
            </BaseHeader>

            <ToggleDialog
                activeToggle={activeToggle}
                isOpen={isActivateDialogOpen}
                onClose={toggleActivateDialog}
                onSave={handleActivate}
                templateKey={
                    template?.emailType && t(`branded-emails.template-list.${emailTypesKeys[template.emailType]}-title`)
                }
                originFromEdition
            />
            <BackDialog
                onSave={handleSave}
                onCancel={handleCancel}
                isOpen={isBackDialogOpen}
                onClose={toggleBackDialog}
                isSubmitting={isSubmitting}
            ></BackDialog>
        </>
    );
}
